import { createContext, useContext } from 'react';
import { Link, NavLink, Outlet, useParams } from 'react-router';
import type { Site } from '@totum-analytics/db';
import { Badge, ErrorNote, Loading } from '../components/ui';
import { useApi } from '../lib/useApi';
import { ModeBadge, siteStatus } from './Sites';

interface SiteContextValue {
  site: Site;
  reload: () => void;
}

const SiteContext = createContext<SiteContextValue | null>(null);

export function useSite(): SiteContextValue {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error('useSite outside SiteLayout');
  return ctx;
}

function Tab({ to, children }: { to: string; children: string }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        `px-3 py-2 text-sm border-b-2 -mb-px transition-colors whitespace-nowrap ${isActive ? 'border-totum-blue text-gray-900 font-medium' : 'border-transparent text-gray-600 hover:text-gray-900'}`
      }
    >
      {children}
    </NavLink>
  );
}

export function SiteLayout() {
  const { id } = useParams();
  const { data: site, error, reload } = useApi<Site>(`/sites/${id}`);

  if (error) return <ErrorNote error={error} onRetry={reload} />;
  if (!site) return <Loading label="Loading site" />;
  const status = siteStatus(site);
  const base = `/sites/${site.id}`;

  return (
    <SiteContext.Provider value={{ site, reload }}>
      <nav aria-label="Breadcrumb" className="text-sm text-gray-500 mb-2">
        <Link to="/" className="text-totum-ink hover:underline">Sites</Link> <span aria-hidden="true">/</span> {site.name}
      </nav>
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <h1 className="text-2xl font-bold text-gray-900">{site.name}</h1>
        <ModeBadge mode={site.identity_mode} />
        <Badge tone={status.tone}>{status.label}</Badge>
      </div>
      <div className="flex gap-1 border-b border-gray-200 mb-6 overflow-x-auto">
        <Tab to={base}>Overview</Tab>
        <Tab to={`${base}/realtime`}>Real time</Tab>
        {site.identity_mode === 'identified' && <Tab to={`${base}/accounts`}>Accounts</Tab>}
        {site.identity_mode === 'identified' && <Tab to={`${base}/accounts/health`}>Needs attention</Tab>}
        {site.identity_mode === 'identified' && <Tab to={`${base}/accounts/adoption`}>Feature adoption</Tab>}
        <Tab to={`${base}/install`}>Install</Tab>
        <Tab to={`${base}/settings`}>Site settings</Tab>
      </div>
      <Outlet />
    </SiteContext.Provider>
  );
}
